import { useEffect, useState } from 'react';
import { Lightbulb, Sparkles } from 'lucide-react';
import { Badge } from '../ui/badge';
import { useApi } from '../../hooks/useApi';

/**
 * Signal Story Card — Document 2 · Phase 2.2
 *
 * One sentence at the top of every Style Opportunity card explaining
 * exactly why this is an opportunity right now. Reads the signal type,
 * location, property type from the opportunity and uses Seller Context
 * brand voice for tone.
 *
 * Live data from GET /api/sales-intelligence/signal-story/:opportunity_id
 * 24h cached server-side; auto-invalidates if signal type or property changes.
 *
 * DRAFT only. Read-only context. Never modifies the opportunity.
 *
 * Props:
 *   - opportunityId: required
 *   - opportunity: optional, used for skeleton-state hint
 *   - variant: 'full' (default, used on OpportunityDetail) | 'compact' (single line)
 */

function formatSignalType(t) {
  if (!t) return 'signal';
  return String(t).replace(/_/g, ' ');
}

function StorySkeleton({ variant }) {
  if (variant === 'compact') {
    return (
      <div className="mt-1 h-3 bg-stone-100 rounded animate-pulse w-3/4" data-testid="signal-story-skeleton" />
    );
  }
  return (
    <div
      className="rounded-sm border border-amber-100 bg-amber-50/40 p-4 sm:p-5 animate-pulse"
      data-testid="signal-story-skeleton"
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-full bg-amber-100" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-amber-100 rounded w-11/12" />
          <div className="h-3 bg-amber-100 rounded w-3/4" />
          <div className="h-2 bg-amber-100/70 rounded w-1/3" />
        </div>
      </div>
    </div>
  );
}

export function SignalStoryCard({ opportunityId, opportunity, variant = 'full' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!opportunityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrored(false);
    (async () => {
      try {
        const res = await api.get(`/sales-intelligence/signal-story/${opportunityId}`);
        if (!mounted) return;
        if (res && res.story) {
          setData(res);
        } else {
          setData(null);
        }
      } catch (e) {
        if (mounted) setErrored(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  if (loading) return <StorySkeleton variant={variant} />;
  if (errored || !data || !data.story) return null;

  const signalType = data.signal_type;

  // Compact variant — one-line subtitle for table rows
  if (variant === 'compact') {
    return (
      <div
        className="mt-1.5 flex items-start gap-2 text-xs text-stone-600"
        data-testid="signal-story-card-compact"
      >
        <Lightbulb className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
        <span className="leading-snug" data-testid="signal-story-text">
          {data.story}
        </span>
      </div>
    );
  }

  // Full variant — main card on OpportunityDetail
  return (
    <div
      className="relative overflow-hidden rounded-sm border border-amber-200 bg-gradient-to-br from-amber-50 via-amber-50/40 to-transparent p-4 sm:p-5"
      data-testid="signal-story-card"
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 via-amber-500 to-amber-300"
        aria-hidden="true"
      />

      <div className="flex items-start gap-3 pl-2">
        <div className="flex-shrink-0 mt-0.5">
          <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center ring-4 ring-amber-50">
            <Lightbulb className="h-4 w-4 text-amber-700" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p
            className="text-sm sm:text-[15px] leading-relaxed text-stone-800 font-medium"
            data-testid="signal-story-text"
          >
            {data.story}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {signalType && (
              <Badge
                variant="outline"
                className="bg-white/80 border-amber-200 text-amber-800 text-[10px] uppercase tracking-wide font-semibold"
                data-testid="signal-story-type-badge"
              >
                signal · {formatSignalType(signalType)}
              </Badge>
            )}
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-stone-500">
              <Sparkles className="h-3 w-3" />
              AI context · review
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignalStoryCard;
